import { Pencil } from "lucide-react";
import { useDelete, useLocaleState, useNotify, useTranslate } from "ra-core";
import { useState } from "react";

import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/atomic-crm/misc/ResponsiveDialog";
import { formatShortDate } from "@/components/market-intel/dates";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toIntlLocale } from "@/lib/dates";

import { SnapshotForm } from "./SocialForms";
import type {
  CompetitorSocialProfile,
  CompetitorSocialSnapshot,
} from "./types";
import { ConfirmDeleteButton, IconAction } from "./ui";

/** Every measurement of a profile, newest first, editable and deletable. */
export const SnapshotHistory = ({
  open,
  onOpenChange,
  profile,
  history,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: CompetitorSocialProfile | null;
  history: readonly CompetitorSocialSnapshot[];
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "es"] = useLocaleState();
  const [remove] = useDelete<CompetitorSocialSnapshot>();
  const [editing, setEditing] = useState<CompetitorSocialSnapshot | null>(null);
  const number = new Intl.NumberFormat(toIntlLocale(locale));
  const title = profile
    ? translate("competition.social.history_title", { handle: profile.handle })
    : "";
  const rows = [...history].reverse();
  const show = (value: number | null | undefined) =>
    value == null ? "—" : number.format(value);

  return (
    <>
      <ResponsiveDialog open={open && !editing} onOpenChange={onOpenChange}>
        <ResponsiveDialogContent
          mobileTitle={title}
          className="sm:max-w-xl md:max-h-[85dvh] md:overflow-y-auto"
        >
          <DialogHeader className="max-md:sr-only">
            <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
          </DialogHeader>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {translate("competition.social.history_empty")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-0 text-sm tabular-nums">
                <thead>
                  <tr className="text-left text-[11px] tracking-[0.04em] text-muted-foreground uppercase">
                    <th className="py-2 pr-2 font-semibold">
                      {translate(
                        "competition.social.snapshot_form.measured_at",
                      )}
                    </th>
                    <th className="px-1 py-2 text-right font-semibold">
                      {translate("competition.social.followers")}
                    </th>
                    <th className="px-1 py-2 text-right font-semibold">
                      {translate("competition.social.posts")}
                    </th>
                    <th className="px-1 py-2 text-right font-semibold">
                      {translate("competition.social.following")}
                    </th>
                    <th>
                      <span className="sr-only">
                        {translate("competition.social.edit")}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/70">
                  {rows.map((snapshot) => (
                    <tr key={snapshot.id}>
                      <td className="py-2 pr-2">
                        <span className="font-medium">
                          {formatShortDate(snapshot.measured_at, locale)}
                        </span>
                        {snapshot.source ? (
                          <span className="block text-xs text-muted-foreground">
                            {snapshot.source}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-1 py-2 text-right font-semibold">
                        {show(snapshot.followers)}
                      </td>
                      <td className="px-1 py-2 text-right">
                        {show(snapshot.posts_count)}
                      </td>
                      <td className="px-1 py-2 text-right">
                        {show(snapshot.following_count)}
                      </td>
                      <td className="py-1 pl-1">
                        <div className="flex justify-end">
                          <IconAction
                            label={translate(
                              "competition.social.snapshot_form.edit_title",
                            )}
                            icon={Pencil}
                            onClick={() => setEditing(snapshot)}
                          />
                          <ConfirmDeleteButton
                            label={translate(
                              "competition.social.delete_measurement",
                            )}
                            onConfirm={() =>
                              remove(
                                "competitor_social_snapshots",
                                { id: snapshot.id, previousData: snapshot },
                                {
                                  mutationMode: "optimistic",
                                  onSuccess: () =>
                                    notify("competition.form.deleted", {
                                      type: "info",
                                    }),
                                  onError: () =>
                                    notify("competition.form.error", {
                                      type: "error",
                                    }),
                                },
                              )
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ResponsiveDialogContent>
      </ResponsiveDialog>
      <SnapshotForm
        open={editing != null}
        onOpenChange={(next) => !next && setEditing(null)}
        profile={profile}
        history={history}
        snapshot={editing}
      />
    </>
  );
};
